import { useEffect, useRef, useState } from "react";
import { Loader2, Search, Users, Plus } from "lucide-react";
import Swal from "sweetalert2";
import { listConversas, criarConversa, nomeConversa } from "../services/whatsappChatApi";

const FILTROS = [
  { id: "todas", label: "Todas" },
  { id: "nao-lidas", label: "Não lidas" },
  { id: "individuais", label: "Individuais" },
  { id: "grupos", label: "Grupos" },
];

const LIMIT = 30;

function useDebounce(valor, delay) {
  const [debounced, setDebounced] = useState(valor);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(valor), delay);
    return () => clearTimeout(timer);
  }, [valor, delay]);
  return debounced;
}

function formatarRelativo(iso) {
  if (!iso) return "";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "";
  const diff = Date.now() - data.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min} min`;
  const agora = new Date();
  const mesmoDia =
    data.getDate() === agora.getDate() &&
    data.getMonth() === agora.getMonth() &&
    data.getFullYear() === agora.getFullYear();
  if (mesmoDia) return data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const ontem = new Date();
  ontem.setDate(agora.getDate() - 1);
  if (
    data.getDate() === ontem.getDate() &&
    data.getMonth() === ontem.getMonth() &&
    data.getFullYear() === ontem.getFullYear()
  ) return "ontem";
  return data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

const PALETAS = [
  "bg-brand-800 text-brand-200",
  "bg-accent-sky/30 text-sky-200",
  "bg-accent-mint/30 text-emerald-200",
  "bg-accent-rose/30 text-rose-200",
  "bg-accent-amber/30 text-amber-200",
  "bg-accent-violet/30 text-violet-200",
];

export function Avatar({ nome, grupo, size = "w-10 h-10 text-sm" }) {
  const inicial = (nome || "?").trim()[0]?.toUpperCase() || "?";
  const indice = inicial.charCodeAt(0) % PALETAS.length;
  return (
    <div
      className={`${size} rounded-full flex items-center justify-center font-bold flex-shrink-0 select-none ${PALETAS[indice]}`}
      aria-hidden="true"
    >
      {grupo ? <Users className="w-4 h-4" /> : inicial}
    </div>
  );
}

function ConversaItem({ conversa, selected, onClick }) {
  const nome = nomeConversa(conversa);
  const preview = conversa.ultima_mensagem_preview || "";
  const hora = formatarRelativo(conversa.ultima_mensagem_em);
  const unread = Number(conversa.nao_lidas) || 0;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      aria-label={`Conversa ${nome}${unread > 0 ? `, ${unread} não lida${unread > 1 ? "s" : ""}` : ""}`}
      className={`w-full text-left flex items-start gap-3 px-3 py-3 border-b border-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${
        selected ? "bg-surface-3" : "hover:bg-surface-2"
      }`}
    >
      <Avatar nome={nome} grupo={conversa.is_grupo} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-sm font-semibold text-ink-1 truncate leading-tight flex items-center gap-1.5">
            {conversa.is_grupo && <Users className="w-3 h-3 text-ink-4 flex-shrink-0" aria-label="Grupo" />}
            <span className="truncate">{nome}</span>
          </span>
          <span className="text-[11px] text-ink-4 flex-shrink-0 tabular-nums">{hora}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-ink-3 truncate">
            {preview || <span className="italic text-ink-5">Sem mensagens</span>}
          </p>
          {unread > 0 && (
            <span
              className="flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-600 text-white text-[10px] font-bold flex items-center justify-center"
              aria-label={`${unread} não lida${unread > 1 ? "s" : ""}`}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function ConversasList({ sessaoId, onSelect, selectedId, socket }) {
  const [conversas, setConversas] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMais, setLoadingMais] = useState(false);
  const [filtro, setFiltro] = useState("todas");
  const [busca, setBusca] = useState("");

  const buscaDebounced = useDebounce(busca, 300);
  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  const ultimoResetRef = useRef(0);
  const sentinelaRef = useRef(null);
  const listaRef = useRef(null);

  async function carregar({ reset = false, cursorOverride = undefined } = {}) {
    const cursorParam = cursorOverride !== undefined ? cursorOverride : (reset ? null : cursor);
    if (reset) setLoading(true);
    else setLoadingMais(true);
    try {
      const { items, nextCursor, hasMore: temMais } = await listConversas(sessaoId, {
        limit: LIMIT,
        filtro,
        cursor: cursorParam || undefined,
        busca: buscaDebounced,
      });
      if (reset) setConversas(items);
      else setConversas((prev) => {
        const ids = new Set(prev.map((c) => c.id));
        return [...prev, ...items.filter((c) => !ids.has(c.id))];
      });
      setCursor(nextCursor);
      setHasMore(temMais ?? items.length === LIMIT);
    } catch {
      // estado vazio
    } finally {
      setLoading(false);
      setLoadingMais(false);
    }
  }

  useEffect(() => {
    if (!sessaoId) return;
    carregar({ reset: true, cursorOverride: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessaoId, filtro, buscaDebounced]);

  // Scroll infinito
  useEffect(() => {
    if (!hasMore || !sentinelaRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMais && !loading && cursor) {
          carregar();
        }
      },
      { root: listaRef.current, threshold: 0.1 },
    );
    obs.observe(sentinelaRef.current);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMais, loading, cursor]);

  // Real-time
  useEffect(() => {
    if (!socket || !sessaoId) return;
    socket.emit("join-conversas-wa", sessaoId);

    function handleMensagemNew(payload) {
      if (!payload || Number(payload.sessao_id) !== Number(sessaoId)) return;
      const cid = payload.conversa_id;
      setConversas((prev) => {
        const idx = prev.findIndex((c) => c.id === cid);
        const msg = payload.mensagem || {};
        const aberta = selectedIdRef.current === cid;
        if (idx === -1) {
          if (payload.conversa && typeof payload.conversa === "object") {
            const nova = { ...payload.conversa, nao_lidas: aberta ? 0 : (payload.conversa.nao_lidas ?? (msg.direcao === "inbound" ? 1 : 0)) };
            return [nova, ...prev];
          }
          const agora = Date.now();
          if (agora - ultimoResetRef.current >= 2000) {
            ultimoResetRef.current = agora;
            setTimeout(() => carregar({ reset: true, cursorOverride: null }), 0);
          }
          return prev;
        }
        const antiga = prev[idx];
        const base = payload.conversa && typeof payload.conversa === "object" ? { ...antiga, ...payload.conversa } : antiga;
        const quando = msg.timestamp_wa || msg.criado_em || new Date().toISOString();
        const preview = msg.texto || (msg.tipo && msg.tipo !== "text" ? `[${msg.tipo}]` : base.ultima_mensagem_preview);
        const atualizada = {
          ...base,
          ultima_mensagem_em: quando,
          ultima_mensagem_preview: preview,
          nao_lidas: aberta
            ? 0
            : (payload.conversa?.nao_lidas ?? ((antiga.nao_lidas ?? 0) + (msg.direcao === "inbound" ? 1 : 0))),
        };
        return [atualizada, ...prev.filter((_, i) => i !== idx)];
      });
    }

    function handleReconnect() {
      socket.emit("join-conversas-wa", sessaoId);
      const agora = Date.now();
      if (agora - ultimoResetRef.current >= 2000) {
        ultimoResetRef.current = agora;
        carregar({ reset: true, cursorOverride: null });
      }
    }

    socket.on("connect", handleReconnect);
    socket.on("mensagem:new", handleMensagemNew);
    return () => {
      socket.off("connect", handleReconnect);
      socket.off("mensagem:new", handleMensagemNew);
      socket.emit("leave-conversas-wa", sessaoId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, sessaoId]);

  // Zera badge local quando o usuário abre a conversa.
  useEffect(() => {
    if (selectedId === null || selectedId === undefined) return;
    setConversas((prev) => prev.map((c) => (c.id === selectedId && c.nao_lidas ? { ...c, nao_lidas: 0 } : c)));
  }, [selectedId]);

  async function handleNovaConversa() {
    const { value: telefone, isConfirmed } = await Swal.fire({
      title: "Nova conversa",
      input: "text",
      inputLabel: "Telefone com DDI e DDD",
      inputPlaceholder: "Ex: 5511999999999",
      showCancelButton: true,
      confirmButtonText: "Abrir",
      cancelButtonText: "Cancelar",
      background: "#15151A",
      color: "#FAFAFA",
      confirmButtonColor: "#6366F1",
      cancelButtonColor: "#2A2A33",
      inputValidator: (v) => {
        const d = String(v || "").replace(/\D/g, "");
        if (d.length < 10 || d.length > 15) return "Informe um telefone válido";
        return undefined;
      },
    });
    if (!isConfirmed || !telefone) return;
    try {
      const conversa = await criarConversa(sessaoId, String(telefone).replace(/\D/g, ""));
      if (conversa?.id) {
        setConversas((prev) => (prev.some((c) => c.id === conversa.id) ? prev : [conversa, ...prev]));
        onSelect(conversa.id);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Não foi possível abrir a conversa",
        text: err?.response?.data?.error?.message || err?.message || "Erro desconhecido",
        background: "#15151A",
        color: "#FAFAFA",
        confirmButtonColor: "#6366F1",
      });
    }
  }

  return (
    <div className="flex flex-col h-full bg-base">
      <div className="px-3 pt-3 pb-2 border-b border-white/[0.06] flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-5 pointer-events-none"
            aria-hidden="true"
          />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou telefone..."
            aria-label="Buscar conversa"
            className="w-full bg-surface-2 border border-white/[0.08] rounded-lg text-xs text-ink-2 pl-8 pr-3 py-1.5 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
          />
        </div>
        <button
          type="button"
          onClick={handleNovaConversa}
          title="Nova conversa"
          aria-label="Nova conversa"
          className="flex-shrink-0 p-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <div role="group" aria-label="Filtrar conversas" className="flex items-center gap-1 px-3 py-2 border-b border-white/[0.06] overflow-x-auto scrollbar-none">
        {FILTROS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setFiltro(id)}
            aria-pressed={filtro === id}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
              filtro === id ? "bg-brand-600 text-white" : "text-ink-3 hover:text-ink-2 hover:bg-surface-2"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div ref={listaRef} className="flex-1 overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-ink-5" />
          </div>
        ) : conversas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-sm text-ink-4">
              {buscaDebounced.trim()
                ? "Nenhuma conversa encontrada para essa busca"
                : filtro !== "todas"
                ? "Nenhuma conversa neste filtro"
                : "Nenhuma conversa gravada ainda"}
            </p>
          </div>
        ) : (
          <>
            {conversas.map((c) => (
              <ConversaItem key={c.id} conversa={c} selected={selectedId === c.id} onClick={() => onSelect(c.id)} />
            ))}
            {hasMore && (
              <div ref={sentinelaRef} className="flex justify-center py-3">
                <Loader2 className="w-4 h-4 animate-spin text-ink-5" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
